import {
  ClaimSummary,
  Repair,
  Activity as ApiActivity,
  ServiceLevelAgreement as ApiServiceLevelAgreement,
  Permit,
} from 'api/resources/models/AutoGenerated';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import UTC from 'dayjs/plugin/utc';
import { ReplacementVehicleIcon } from '../../components/ReplacementVehicleIcon';
import { formatDateTime } from 'utils/helpers';
import { getCpdDetailsStatus } from 'components/VehiclesTable/fields/Cpd';
import { DateStatus } from 'components/VehiclesTable/fields/types';
import { getDeliveredDetailsStatus } from 'components/VehiclesTable/fields/Delivered';
import { getEcdDetailsStatus } from 'components/VehiclesTable/fields/Ecd';
import { getCompletedDetailsStatus } from 'components/VehiclesTable/fields/Completed';
import { getOnSiteDetailsStatus } from 'components/VehiclesTable/fields/onSite';
import { getBookedInDetailsStatus } from 'components/VehiclesTable/fields/bookedIn';
import {
  AdditionalStatus,
  additionalStatus,
} from 'components/VehiclesTable/fields/additionalStatus';
import WarningAmber from '@mui/icons-material/WarningRounded';
dayjs.extend(localizedFormat);
dayjs.extend(UTC);

const locales = (locale: string) => import(`dayjs/locale/${locale}.js`);

// Expected output: 'en-gb-oxendict' -> 'en-gb', 'en-us' -> 'en', 'fr' -> 'fr'
const simplifyLanguageZone = (locale: string): string => {
  const langParts = locale.toLowerCase().split('-');
  if (langParts.length > 2) {
    return `${langParts[0]}-${langParts[1]}`;
  }
  return langParts[0];
}

const currentLocale = simplifyLanguageZone(navigator.language);

locales(currentLocale).then(() => {
  dayjs.locale(currentLocale);
});

export type Activity = {
  activityDate: Date;
  message: string | null;
  operator: string | null;
  icon: string | null;
  category: string | null;
  complaint: boolean;
  activityCategory: string | null;
  activityType: string | null;
  externalShortCode: string | null;
};

export type ServiceLevelAgreement = {
  title?: string | null;
  description?: string | null;
  startDate?: string;
  expectedValue?: string;
  actualValue?: string;
  status?: string | null;
  icon?: string | null;
};

export type QuickDetails = {
  title: string;
  value?: string | JSX.Element | null;
  permit?: Permit;
};

export type Milestone = {
  name: string;
  date?: string;
  status?: DateStatus;
  additionalStatus?: AdditionalStatus;
  permit?: Permit;
  additionalMessage?: string | null;
};

export type Information = {
  name: string;
  value: string | JSX.Element | number;
  permit?: Permit;
};

export const mapQuickDetails = (data: ClaimSummary): QuickDetails[] => {
  return [
    {
      title: 'Owner',
      value: data.ownerName,
      permit: Permit.FLV_OWNER,
    },
    {
      title: 'Repair Code',
      value: data.repairCode,
    },
    {
      title: 'Vehicle Registration',
      value: data.vehicleRegistration,
    },
    {
      title: 'Vehicle',
      value: data.vehicle,
    },
    { title: 'Stream', value: data.stream },
    {
      title: 'Claim Reference',
      value: data.claimReference,
    },
  ];
};

export const mapMilestoneDetails = (
  data: ClaimSummary,
  details: Repair
): Milestone[] => {
  const now = dayjs();
  const getDate = (date: null | string | Date | undefined) =>
    date ? dayjs(date) : undefined;

  return [
    {
      name: 'FNOL',
      date: formatDateTime(data.milestone?.notificationDate),
    },
    {
      name: 'Booked In',
      date: formatDateTime(data.milestone?.bookedInDate),
      status: getBookedInDetailsStatus(
        now,
        getDate(data.milestone?.onSiteDate),
        getDate(data.milestone?.bookedInDate)
      ),
    },
    {
      name: 'On Site',
      date: formatDateTime(data.milestone?.onSiteDate),
      status: getOnSiteDetailsStatus(
        now,
        getDate(data.milestone?.bookedInDate),
        getDate(data.milestone?.onSiteDate)
      ),
      additionalStatus: additionalStatus(
        getDate(data.milestone?.bookedInDate),
        getDate(data.milestone?.onSiteDate)
      ),
    },
    {
      name: 'Estimated',
      date: formatDateTime(data.milestone?.estimationDate),
    },
    {
      name: 'Authorised',
      date: formatDateTime(data.milestone?.insAuthorisedDate),
    },
    {
      name: 'Parts Ordered',
      date: formatDateTime(data.milestone?.partsOrderedDate),
    },
    {
      name: 'Work Start',
      date: formatDateTime(data.milestone?.startedDate),
    },
    {
      name: 'E.C.D.',
      date: formatDateTime(details?.dtgEcd),
      status: getEcdDetailsStatus(
        now,
        getDate(details?.dtgEcd),
        getDate(details?.dtgCompleted)
      ),
    },
    {
      name: 'Work Done',
      date: formatDateTime(details.dtgCompleted),
      status: getCompletedDetailsStatus(
        now,
        getDate(details?.dtgEcd),
        getDate(details?.dtgCompleted)
      ),
      additionalStatus: additionalStatus(
        getDate(details.dtgEcd),
        getDate(details.dtgCompleted)
      ),
    },
    {
      name: 'Booked Out / C.P.D',
      date: formatDateTime(details?.dtgCpd),
      status: getCpdDetailsStatus(
        now,
        getDate(details.dtgCpd),
        getDate(details.dtgDelivered)
      ),
      additionalMessage: data?.milestone?.cpdChangeCount
        ? `Changed ${data?.milestone?.cpdChangeCount} times`
        : null,
    },
    {
      name: 'Delivered',
      date: formatDateTime(data.milestone?.deliveredDate),
      status: getDeliveredDetailsStatus(
        now,
        getDate(details.dtgCpd),
        getDate(details.dtgDelivered)
      ),
      additionalStatus: additionalStatus(
        getDate(details.dtgCpd),
        getDate(details.dtgDelivered)
      ),
    },
  ];
};

export const mapClaimInformation = (
  data: ClaimSummary,
  repairDetails: Repair
): Information[] => {
  return [
    {
      name: 'Claim ID',
      value: data?.claimId ?? '-',
    },
    {
      name: 'Owner Excess',
      value: data?.ownerExcess ?? '-',
    },
    {
      name: 'Owner Paid',
      value: data?.ownerExcessPaid ?? '-',
    },
    {
      name: 'Possible Total Loss',
      value: data?.possibleTL ? <WarningAmber className="image" /> : '-',
    },
    {
      name: 'Repair Stage',
      value: repairDetails.repairStage ? repairDetails.repairStage : '-',
    },
    {
      name: 'Repairer Group',
      value: data?.repairer?.group ?? '-',
    },
    {
      name: 'Repairer Name',
      value: data?.repairer?.name ?? '-',
    },
  ];
};

export const mapMobilityInformation = (
  data: ClaimSummary,
  repair: Repair
): Information[] => {
  const isAnyOfCourtesyMakeOrModel =
    !!data.replacementVehicle?.courtesyMake ||
    !!data.replacementVehicle?.courtesyModel;
  return [
    {
      name: 'Drivable',
      value:
        data.isVehicleDriveable === null
          ? '-'
          : data.isVehicleDriveable
          ? 'Yes'
          : 'No',
    },
    {
      name: 'Replacement Vehicle',
      value: isAnyOfCourtesyMakeOrModel
        ? `${data.replacementVehicle?.courtesyMake ?? ''} ${
            data.replacementVehicle?.courtesyModel ?? ''
          }`.trim()
        : '-',
    },
    {
      name: 'Replacement Vehicle Reg. No.',
      value: data.replacementVehicle?.replacementReg?.trim()
        ? data.replacementVehicle?.replacementReg
        : '-',
    },
    {
      name: 'Status',
      value: (
        <ReplacementVehicleIcon status={repair.replacementVehicleStatus} />
      ),
    },
  ];
};

export const mapActivity = (activity: ApiActivity): Activity => {
  return {
    activityDate: dayjs(activity.activityDate).toDate(),
    operator: activity.operator,
    message: activity.message,
    icon: activity.icon,
    category: activity.activityCategory,
    complaint: activity.complaint,
    activityCategory: activity.activityCategory,
    activityType: activity.activityType,
    externalShortCode: activity.externalShortCode,
  };
};

export const mapSlas = (item: ApiServiceLevelAgreement) => {
  return {
    title: item.title ?? '',
    description: item.description ?? '',
    startDate: formatDateTime(item.startDate),
    expectedValue: formatDateTime(item.expectedValue),
    actualValue: formatDateTime(item.actualValue),
    icon: item.icon,
    status: item.status,
  };
};
